/**
 * useActionsColumnPreference Hook
 *
 * Manages GLOBAL Actions column visibility preference in localStorage.
 * This preference applies across ALL MetadataTable instances - it's not per-table.
 *
 * Design decisions:
 * - Storage key: 'rp-table-actions-visible' (follows global rp- prefix pattern)
 * - Default: visible (true) - only writes to localStorage when hiding
 * - Removing key = reset to default (visible)
 */

import { useState, useCallback } from 'react'

/**
 * localStorage key for global actions column visibility
 * Uses 'rp-' prefix to indicate it's a global Rocket Pricing preference
 */
const STORAGE_KEY = 'rp-table-actions-visible'

/**
 * Default visibility state (visible)
 */
const DEFAULT_VISIBLE = true

/**
 * Load actions column visibility from localStorage
 */
function loadActionsVisibility(): boolean {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    // Only 'false' means hidden; null/undefined/anything else = visible (default)
    return stored === 'false' ? false : DEFAULT_VISIBLE
  } catch {
    // localStorage unavailable - return default
    return DEFAULT_VISIBLE
  }
}

/**
 * Save actions column visibility to localStorage
 * Only persists when hiding (false) - removing key = default (visible)
 */
function saveActionsVisibility(visible: boolean): void {
  try {
    if (visible === DEFAULT_VISIBLE) {
      // Remove key to reset to default (reduces localStorage clutter)
      localStorage.removeItem(STORAGE_KEY)
    } else {
      localStorage.setItem(STORAGE_KEY, String(visible))
    }
  } catch {
    // localStorage unavailable - preference won't persist but app continues
  }
}

/**
 * Hook to manage global Actions column visibility preference.
 *
 * This is a GLOBAL setting that applies to ALL MetadataTable instances.
 * The preference persists in localStorage and survives page refreshes.
 *
 * @returns Object with:
 *   - actionsVisible: Current visibility state
 *   - setActionsVisible: Function to update visibility (persists to localStorage)
 *
 * @example
 * ```tsx
 * const { actionsVisible, setActionsVisible } = useActionsColumnPreference()
 *
 * // In table column generation:
 * if (actionsVisible && hasActions) {
 *   columns.push(actionsColumn)
 * }
 *
 * // In column customizer:
 * <Checkbox
 *   checked={actionsVisible}
 *   onChange={(e) => setActionsVisible(e.target.checked)}
 * />
 * ```
 */
export function useActionsColumnPreference() {
  // Initialize from localStorage
  const [actionsVisible, setActionsVisibleState] = useState<boolean>(() => {
    return loadActionsVisibility()
  })

  // Update visibility and persist to localStorage
  const setActionsVisible = useCallback((visible: boolean) => {
    setActionsVisibleState(visible)
    saveActionsVisibility(visible)
  }, [])

  return {
    actionsVisible,
    setActionsVisible,
  }
}

/**
 * Clear the actions column visibility preference (reset to default).
 * Useful for testing or resetting user data.
 */
export function clearActionsColumnPreference(): void {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // localStorage unavailable - nothing to clear
  }
}
